import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import { doc, getDoc, updateDoc, arrayRemove, onSnapshot } from "firebase/firestore";
import { useTheme } from "../../context/ThemeContext";

export default function BlockedUsers({ onClose }) {
    const [blockedIds, setBlockedIds] = useState([]);
    const [blockedUsers, setBlockedUsers] = useState([]);
    const { isDarkMode } = useTheme();

    // 1. Listen to My Blocked List
    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        const unsub = onSnapshot(doc(db, "users", uid), (d) => {
            if (d.exists()) {
                setBlockedIds(d.data().blockedUsers || []);
            }
        });
        return () => unsub();
    }, []);

    // 2. Fetch User Details for Blocked IDs
    useEffect(() => {
        const fetchDetails = async () => {
            if (blockedIds.length === 0) {
                setBlockedUsers([]);
                return;
            }

            const details = [];
            for (const id of blockedIds) {
                try {
                    const userSnap = await getDoc(doc(db, "users", id));
                    if (userSnap.exists()) {
                        details.push({ id, ...userSnap.data() });
                    } else {
                        details.push({ id, username: "Unknown User" });
                    }
                } catch (e) {
                    console.error(e);
                }
            }
            setBlockedUsers(details);
        };
        fetchDetails();
    }, [blockedIds]);

    const handleUnblock = async (uid, name) => {
        if (!window.confirm(`Unblock ${name}?`)) return;
        try {
            await updateDoc(doc(db, "users", auth.currentUser.uid), {
                blockedUsers: arrayRemove(uid)
            });
        } catch (e) {
            console.error("Unblock failed", e);
            alert("Failed to unblock.");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
            <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-[#1e293b] text-white' : 'bg-white text-gray-800'}`}>

                {/* Header */}
                <div className={`px-6 py-4 flex justify-between items-center border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <span className="text-red-500">🚫</span> Blocked Users
                    </h2>
                    <button onClick={onClose} className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                {/* List */}
                <div className="max-h-[60vh] overflow-y-auto custom-scrollbar p-2">
                    {blockedUsers.length === 0 ? (
                        <div className="p-8 text-center opacity-50 flex flex-col items-center">
                            <div className="text-4xl mb-2">🛡️</div>
                            <p>No blocked users.</p>
                        </div>
                    ) : (
                        blockedUsers.map(user => (
                            <div key={user.id} className={`flex items-center justify-between p-3 rounded-xl mb-1 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                                <div className="flex items-center gap-3">
                                    {user.photoURL ? (
                                        <img src={user.photoURL} alt={user.username} className="w-10 h-10 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-gray-500 flex items-center justify-center text-white font-bold">
                                            {user.username?.[0]?.toUpperCase() || "?"}
                                        </div>
                                    )}
                                    <div>
                                        <div className="font-bold text-sm">{user.username}</div>
                                        <div className="text-xs opacity-50">Blocked</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleUnblock(user.id, user.username)}
                                    className="px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors"
                                >
                                    Unblock
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
